import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import {
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import {
  CatalogImageOwner,
  useFloorplannerProductLibraryFilters,
  useFloorplannerProducts,
} from "../../features/floorplan3d/catalog/catalogQueries";
import { insertUserModel3D } from "../../features/floorplan3d/userModels/insertUserModel3D";
import { UserModel3DLibraryItem } from "../../features/floorplan3d/userModels/userModel3DTypes";
import { useCommunityUserModels3D } from "../../gql/userModel3D";
import { CommunityModelCard } from "./productLibrary/CommunityModelCard";
import { CommunityModelDetailModal } from "./productLibrary/CommunityModelDetailModal";
import { ProductLibraryCard } from "./productLibrary/ProductLibraryCard";
import { ProductLibraryDetailModal } from "./productLibrary/ProductLibraryDetailModal";
import { ProductLibraryFilters } from "./productLibrary/ProductLibraryFilters";
import { insertPublishedProduct3D } from "./productLibrary/insertPublishedProduct3D";
import {
  LibraryFilterFacets,
  LibraryFilterState,
} from "./productLibrary/libraryTypes";
import {
  compoundCategoryIds,
  toCategoryTree,
  toFilterOptions,
} from "./productLibrary/filterUtils";
import {
  getProductLibrarySession,
  patchProductLibrarySession,
  ProductLibraryCommunitySort,
  ProductLibrarySortBy,
} from "./productLibrary/productLibrarySession";
import { useDebouncedValue } from "./productLibrary/useDebouncedValue";

export { insertPublishedProduct3D } from "./productLibrary/insertPublishedProduct3D";

type Props = {
  position?: { top: number; left: number };
  onClose: () => void;
  dropdownRef?: React.Ref<HTMLDivElement>;
  selectedSource?: LibrarySource;
  onSourceChange?: (source: LibrarySource) => void;
};

export type LibrarySource = "BRAND" | "BONI" | "USERS";

const PAGE_SIZE = 24;
const PANEL_WIDTH = 600;
/** Delay before search text hits the GraphQL query (keeps typing responsive). */
const SEARCH_DEBOUNCE_MS = 300;

const emptyFacets = (): LibraryFilterFacets => ({
  categories: [],
  brands: [],
  suppliers: [],
  colors: [],
  shapes: [],
  materials: [],
  styles: [],
  patterns: [],
  rooms: [],
  priceMin: null,
  priceMax: null,
});

const SOURCE_TABS: Array<{ id: LibrarySource; labelKey: string }> = [
  { id: "BONI", labelKey: "productLibrary.byBoni" },
  { id: "BRAND", labelKey: "productLibrary.byBrands" },
  { id: "USERS", labelKey: "productLibrary.byUsers" },
];

const LibraryTabs: React.FC<{
  value: LibrarySource;
  onChange: (source: LibrarySource) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
  <div
    role="tablist"
    aria-label={t("productLibrary.sources")}
    className="grid grid-cols-3 gap-2"
  >
    {SOURCE_TABS.map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={value === tab.id}
        onClick={() => onChange(tab.id)}
        className={`min-h-10 rounded-md border px-2 py-2 text-xs font-semibold leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111] ${
          value === tab.id
            ? "border-boniYellowPrimary bg-boniYellowPrimary text-black"
            : "border-gray-300 bg-white text-gray-700 hover:border-gray-500"
        }`}
      >
        {t(tab.labelKey)}
      </button>
    ))}
  </div>
  );
};

export const ProductLibraryPanel: React.FC<Props> = observer(
  ({
    position,
    onClose,
    dropdownRef,
    selectedSource,
    onSourceChange,
  }) => {
    const { t } = useTranslation();
    const searchRef = React.useRef<HTMLInputElement>(null);
    const [localSource, setLocalSource] =
      React.useState<LibrarySource>("BONI");
    const source = selectedSource ?? localSource;
    const selectSource = onSourceChange ?? setLocalSource;
    const [search, setSearchState] = React.useState(
      () => getProductLibrarySession().search,
    );
    const debouncedSearch = useDebouncedValue(
      search.trim(),
      SEARCH_DEBOUNCE_MS,
    );
    const [filters, setFiltersState] = React.useState<LibraryFilterState>(
      () => ({ ...getProductLibrarySession().filters }),
    );
    const [sortBy, setSortByState] = React.useState<ProductLibrarySortBy>(
      () => getProductLibrarySession().sortBy,
    );
    const [communitySort, setCommunitySortState] =
      React.useState<ProductLibraryCommunitySort>(
        () => getProductLibrarySession().communitySort,
      );
    const [page, setPageState] = React.useState(
      () => getProductLibrarySession().page,
    );
    const [message, setMessage] = React.useState<string>();
    const [detailProduct, setDetailProduct] =
      React.useState<CatalogImageOwner>();
    const [detailCommunityModel, setDetailCommunityModel] =
      React.useState<UserModel3DLibraryItem>();

    const setSearch = React.useCallback((value: string) => {
      setSearchState(value);
      patchProductLibrarySession({ search: value });
    }, []);
    const setFilters = React.useCallback((value: LibraryFilterState) => {
      setFiltersState(value);
      patchProductLibrarySession({ filters: value });
    }, []);
    const setSortBy = React.useCallback((value: ProductLibrarySortBy) => {
      setSortByState(value);
      patchProductLibrarySession({ sortBy: value });
    }, []);
    const setCommunitySort = React.useCallback(
      (value: ProductLibraryCommunitySort) => {
        setCommunitySortState(value);
        patchProductLibrarySession({ communitySort: value });
      },
      [],
    );
    const setPage = React.useCallback(
      (value: number | ((current: number) => number)) => {
        setPageState((current) => {
          const next = typeof value === "function" ? value(current) : value;
          patchProductLibrarySession({ page: next });
          return next;
        });
      },
      [],
    );

    const isUsers = source === "USERS";
    const catalogOrigin = source === "USERS" ? undefined : source;
    const filterQuery = useFloorplannerProductLibraryFilters(
      catalogOrigin,
      isUsers,
    );
    const productVariables = React.useMemo(() => {
      const categoryIds = compoundCategoryIds(filters);
      return {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        search: debouncedSearch || undefined,
        categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
        supplierIds:
          filters.supplierIds.length > 0 ? filters.supplierIds : undefined,
        brands:
          filters.brandNames.length > 0 ? filters.brandNames : undefined,
        priceRange:
          filters.priceFrom != null || filters.priceTo != null
            ? { from: filters.priceFrom, to: filters.priceTo }
            : undefined,
        sortBy,
        hasPublished3DAsset: true as const,
        product3DLibraryOrigin: catalogOrigin,
      };
    }, [catalogOrigin, debouncedSearch, filters, page, sortBy]);
    const productsQuery = useFloorplannerProducts(productVariables, isUsers);
    const communityQuery = useCommunityUserModels3D(
      {
        input: {
          search: debouncedSearch || undefined,
          sortBy: communitySort,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      },
      !isUsers,
    );

    const products = (productsQuery.data?.productsMeta?.data ??
      []) as CatalogImageOwner[];
    const readyProducts = products.filter(
      (product) =>
        product.active3DAsset?.status === "ready" &&
        Boolean(product.active3DAsset.glbUrl),
    );
    const communityModels = (communityQuery.data?.communityUserModels3D
      ?.data ?? []) as UserModel3DLibraryItem[];
    const queriedTotal = isUsers
      ? Number(communityQuery.data?.communityUserModels3D?.total ?? 0)
      : Number(productsQuery.data?.productsMeta?.total ?? 0);
    /** True while the typed search has not yet been sent to the server. */
    const searchPending = search.trim() !== debouncedSearch;
    const loading =
      searchPending ||
      (isUsers ? communityQuery.loading : productsQuery.loading);
    const error = isUsers ? communityQuery.error : productsQuery.error;
    const visibleCount = isUsers
      ? communityModels.length
      : readyProducts.length;
    // Ignore Apollo's previous total while refetching, and when the grid is empty.
    const total = loading || visibleCount === 0 ? 0 : queriedTotal;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const publishedFilters =
      filterQuery.data?.publishedProduct3DLibraryFilters;
    const facets: LibraryFilterFacets = publishedFilters
      ? {
          categories: toCategoryTree(publishedFilters.categories),
          brands: toFilterOptions(publishedFilters.brands),
          suppliers: toFilterOptions(publishedFilters.suppliers),
          colors: toFilterOptions(publishedFilters.colors),
          shapes: toFilterOptions(publishedFilters.shapes),
          materials: toFilterOptions(publishedFilters.materials),
          styles: toFilterOptions(publishedFilters.styles),
          patterns: toFilterOptions(publishedFilters.patterns),
          rooms: toFilterOptions(publishedFilters.rooms),
          priceMin: publishedFilters.priceMin,
          priceMax: publishedFilters.priceMax,
        }
      : emptyFacets();

    // Reset page when browse inputs change, but not on the first mount so a
    // restored session page survives close → reopen.
    const skipPageResetRef = React.useRef(true);
    React.useEffect(() => {
      if (skipPageResetRef.current) {
        skipPageResetRef.current = false;
        return;
      }
      setPage(0);
    }, [
      communitySort,
      debouncedSearch,
      filters,
      setPage,
      sortBy,
      source,
    ]);
    React.useEffect(() => {
      searchRef.current?.focus();
      const handleKey = (event: KeyboardEvent) => {
        if (
          event.key !== "Escape" ||
          detailProduct ||
          detailCommunityModel
        )
          return;
        event.preventDefault();
        onClose();
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }, [detailCommunityModel, detailProduct, onClose]);

    const setRefs = (element: HTMLDivElement | null) => {
      if (typeof dropdownRef === "function") dropdownRef(element);
      else if (dropdownRef && "current" in dropdownRef) {
        (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current =
          element;
      }
    };

    const insertProduct = (product: CatalogImageOwner) => {
      const result = insertPublishedProduct3D(product);
      if (!result.ok) setMessage(result.reason);
    };

    const insertCommunityModel = (model: UserModel3DLibraryItem) => {
      const result = insertUserModel3D(model);
      if (!result.ok) setMessage(result.reason);
    };

    const hasNoResults = !loading && !error && visibleCount === 0;

    return (
      <>
        <div
          ref={setRefs}
          role="dialog"
          aria-modal="true"
          aria-label={t("productLibrary.title")}
          onClick={(event) => event.stopPropagation()}
          className="fixed z-50 flex min-h-0 flex-col overflow-hidden rounded-md border border-stone-200 bg-white shadow-xl max-lg:!inset-0 max-lg:!h-full max-lg:!w-full max-lg:!rounded-none max-lg:!border-0"
          style={{
            top: 16,
            bottom: 16,
            left: position?.left ?? 180,
            width: PANEL_WIDTH,
            maxWidth: `calc(100vw - ${(position?.left ?? 180) + 16}px)`,
          }}
        >
          <div className="shrink-0 border-b border-gray-200 bg-white p-4">
            <div className="mb-4">
              <LibraryTabs value={source} onChange={selectSource} />
            </div>
            {isUsers ? (
              <div className="flex items-stretch gap-2">
                <label className="relative block min-w-0 flex-1">
                  <span className="sr-only">{t("productLibrary.searchShared")}</span>
                  <FiSearch
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    ref={searchRef}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("productLibrary.searchTitles")}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-[#111] outline-none placeholder:text-gray-400 focus:border-[#111] focus:ring-1 focus:ring-[#111]"
                  />
                </label>
                <select
                  value={communitySort}
                  aria-label={t("productLibrary.sortShared")}
                  onChange={(event) =>
                    setCommunitySort(event.target.value as "NAME" | "RECENT")
                  }
                  className="h-10 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-700 focus:border-[#111] focus:ring-[#111]"
                >
                  <option value="RECENT">{t("common.newest")}</option>
                  <option value="NAME">{t("common.name")}</option>
                </select>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("productLibrary.close")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
                >
                  <IoClose size={18} />
                </button>
              </div>
            ) : (
              <ProductLibraryFilters
                search={search}
                onSearchChange={setSearch}
                searchRef={searchRef}
                filters={filters}
                onChange={setFilters}
                facets={facets}
                sortBy={sortBy}
                onSortChange={setSortBy}
                productCount={total}
                loading={loading}
                onClose={onClose}
                showAllFilterControls
              />
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col bg-[#F7F6F4]">
            {message && (
              <div
                role="status"
                className="mx-3 mt-3 flex shrink-0 items-start justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              >
                <span>{message}</span>
                <button
                  type="button"
                  onClick={() => setMessage(undefined)}
                  className="font-medium underline"
                >
                  {t("common.dismiss")}
                </button>
              </div>
            )}
            {error && (
              <div className="mx-3 mt-3 shrink-0 rounded-md border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-800">
                {t("productLibrary.loadError")}
              </div>
            )}
            {hasNoResults ? (
              <div className="flex flex-1 flex-col items-center justify-center px-8 py-14 text-center">
                <FiBox size={30} className="text-zinc-500" />
                <h3 className="mt-3 text-sm font-semibold text-zinc-900">
                  {isUsers
                    ? t("productLibrary.noSharedMatch")
                    : t("productLibrary.noPublishedMatch")}
                </h3>
                <p className="mt-1 max-w-sm text-sm text-zinc-600">
                  {t("productLibrary.tryAnotherSearch")}
                </p>
              </div>
            ) : (
              <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-2 content-start items-start gap-2 overflow-y-auto overscroll-contain p-2 pb-8 sm:gap-3 sm:p-3 lg:gap-4">
                {/* Skip stale Apollo rows while a new search/page request is in flight. */}
                {!loading &&
                  (isUsers
                    ? communityModels.map((model) => (
                        <CommunityModelCard
                          key={model.id}
                          model={model}
                          onInsert={insertCommunityModel}
                          onMoreInfo={setDetailCommunityModel}
                        />
                      ))
                    : readyProducts.map((product) => (
                        <ProductLibraryCard
                          key={product.id}
                          product={product}
                          onInsert={insertProduct}
                          onMoreInfo={setDetailProduct}
                        />
                      )))}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-between border-t border-stone-200 bg-white px-4 py-3">
            <button
              type="button"
              disabled={loading || page === 0 || total === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="inline-flex min-h-11 items-center gap-1 rounded-sm px-3 py-2 text-xs font-medium text-zinc-800 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-[#111] disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
            >
              <FiChevronLeft /> {t("common.previous")}
            </button>
            <span className="text-xs text-zinc-600">
              {t("common.pageOf", {
                current: total === 0 ? 1 : Math.min(page + 1, pages),
                total: pages,
              })}
            </span>
            <button
              type="button"
              disabled={loading || total === 0 || page + 1 >= pages}
              onClick={() =>
                setPage((current) => Math.min(pages - 1, current + 1))
              }
              className="inline-flex min-h-11 items-center gap-1 rounded-sm px-3 py-2 text-xs font-medium text-zinc-800 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-[#111] disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
            >
              {t("common.next")} <FiChevronRight />
            </button>
          </div>
        </div>
        {detailProduct && (
          <ProductLibraryDetailModal
            product={detailProduct}
            onClose={() => setDetailProduct(undefined)}
          />
        )}
        {detailCommunityModel && (
          <CommunityModelDetailModal
            model={detailCommunityModel}
            onClose={() => setDetailCommunityModel(undefined)}
          />
        )}
      </>
    );
  },
);
